import React from 'react';

import { useQuery } from '@apollo/client';
import { Container } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { FacebookShareButton, LinkedinShareButton, TwitterShareButton } from 'react-share';

import FacebookIcon from '../../assets/icons/FacebookIcon';
import LinkedInIcon from '../../assets/icons/LinkedInIcon';
import TwitterIcon from '../../assets/icons/TwitterIcon';
import { GET_NEWS_BY_ID } from '../../graphql/news';
import { HEADER_IMG_SRC_SET } from '../../pages/DiscoverPage/const';
import { BREAKPOINTS_VALUES } from '../../theme';
import { formatDateToNewsFormat } from '../../utils/helpers';
import IKImage from '../IKImage/IKImage';
import { SeoBlock } from '../SeoBlock';
import useStyles from './styles';

const BLOCKS = [
  {
    layout: 'image',
    Component: (data) => (
      <IKImage
        path={data.data.image}
        alt={data.data.alt}
        draggable={false}
        srcset={HEADER_IMG_SRC_SET}
        sizes={`(max-width:${BREAKPOINTS_VALUES['sm']}px) 500px, (max-width:${BREAKPOINTS_VALUES['md']}px) 800px, (max-width:${BREAKPOINTS_VALUES['lg']}px) 1100px, 1200px`}
      />
    ),
  },
  {
    layout: 'text',
    Component: (data) => <>{parse(data?.data?.text_content)}</>,
  },
];

const NewsItem = () => {
  const params = useParams();
  const postId = params.id;

  const currentLocation = window?.location?.href || '';

  const {
    data: post,
    // error
  } = useQuery(GET_NEWS_BY_ID, {
    variables: { id: postId },
  });
  const { t } = useTranslation();
  const classes = useStyles();
  const blogPost = post?.getBlogPost;
  const user = blogPost?.user;
  const postContent = blogPost?.post_content && JSON.parse(blogPost?.post_content);
  const seoTitle = blogPost?.seo_title;
  const seoDescription = blogPost?.seo_description;
  const seoImage = blogPost?.seo_image;
  const formattedDate = formatDateToNewsFormat(blogPost?.published_at);

  return (
    <Container className={classes.container} maxWidth={false}>
      <SeoBlock
        data={{
          seo_title: seoTitle,
          seo_description: seoDescription,
          share_image: seoImage,
        }}
        canonicalUrl={`/news/${post?.id}`}
      />

      <Box className={classes.wrapper}>
        {blogPost?.image_url && (
          <IKImage
            className={classes.img}
            path={blogPost?.image_url}
            draggable={false}
            srcset={HEADER_IMG_SRC_SET}
            sizes="100vw"
            alt={blogPost?.title}
          />
        )}

        <Container className={classes.containerIntro}>
          {user?.avatar ? (
            <div className={classes.roundImageWrapper}>
              <IKImage
                path={user?.avatar}
                alt={user?.name}
                draggable={false}
                srcset={[{ width: 60 }]}
                sizes={'60px'}
              />
            </div>
          ) : (
            <AccountCircleIcon className={classes.roundImageWrapper} />
          )}
          <div className={classes.textWrapper}>
            <Typography variant="h1" component="h1" align="center" className={classes.title}>
              {blogPost?.title}
            </Typography>

            <Typography component="p" align="center" className={classes.authorName}>
              {user?.name}
            </Typography>

            <Typography component="p" align="center" className={classes.publishedAt}>
              {formattedDate}
            </Typography>
          </div>
        </Container>
        <div className={classes.gradient} />
        <div className={classes.topGradient} />
        <div className={classes.bottomGradient} />
      </Box>
      <Box className={classes.newsContent}>
        <Box className={classes.descriptionBlock}>
          <Typography component="p" align="left" className={classes.introductionText}>
            {blogPost?.post_introduction}
          </Typography>

          {blogPost?.image_url && (
            <IKImage
              className={classes.introductionImage}
              path={blogPost?.image_url}
              alt={blogPost?.title}
              draggable={false}
              srcset={[{ width: 292 }, { width: 640 }, { width: 760 }, { width: 780 }]}
              sizes={`(max-width:${BREAKPOINTS_VALUES['sm']}px) 292px, (max-width:${BREAKPOINTS_VALUES['md']}px) 640px, (max-width:${BREAKPOINTS_VALUES['lg']}px) 760px, 780px`}
            />
          )}
        </Box>
        <Box className={classes.containerContent}>
          <>
            {postContent &&
              postContent.map((block) => {
                const desiredBlock = BLOCKS.find((item) => item.layout === block.layout);
                const Component: (({ data }: { data: any }) => JSX.Element | null) | undefined =
                  desiredBlock?.Component;

                if (!Component) return null;
                return <Component key={block.key} data={block.attributes} />;
              })}
          </>
        </Box>
        <Box className={classes.socialContainer}>
          <Typography component="p" className={classes.shareText}>
            {t('page.news.shareTitle', { title: blogPost?.title })}
          </Typography>
          <div className={classes.shareButtons}>
            <LinkedinShareButton
              title={blogPost?.title}
              url={currentLocation}
              resetButtonStyle={true}
            >
              <LinkedInIcon color={'#7384c3'} />
            </LinkedinShareButton>
            <TwitterShareButton
              title={blogPost?.title}
              url={currentLocation}
              resetButtonStyle={true}
            >
              <TwitterIcon color={'#7384c3'} />
            </TwitterShareButton>
            <FacebookShareButton
              quote={blogPost?.title}
              url={currentLocation}
              resetButtonStyle={true}
            >
              <FacebookIcon color={'#7384c3'} />
            </FacebookShareButton>
          </div>
        </Box>
      </Box>
    </Container>
  );
};

export default NewsItem;
